//
// utility functions

import { SchoolModel, UserProfileType } from '@peekapak/types/DataProtocolTypes';
import {
  CurrentYearTeacher,
  DistrictData,
  DistrictDataBySchool,
  JournalData,
  MiddleSchoolCheckInEntry,
  ReportStudent,
  ReportingMixpanelEvent,
  TeacherClassroomMaps,
} from '@peekapak/types/ReportDataExchangeTypes';
import { PeekavilleJournalEntry, StudentProgressData } from '@peekapak/types/PeekavilleGameDataTypes';
import { Assignment } from '@peekapak/types/LessonPlanTypes';
import { logDebugLevel, noWhiteSpace } from '../GlobalFunctions';
import { EventName } from '@peekapak/types/AnalyticsTypes';

export interface ChartDataCalculationParams {
  data: DistrictDataByType;
  startDate: Date;
  endDate: Date;
  schoolList: SchoolModel[];
  minBarWidth: number;
  gapWidth: number;
  containerWidth: number;
  forcedNumberOfDaysInCount?: number;
}

export type SchoolChartDataCalculationParams = Omit<ChartDataCalculationParams, 'data' | 'schoolList'>;

interface DistrictDataByType {
  number_of_educators: ReportingMixpanelEvent[];
  educator_logins: ReportingMixpanelEvent[];
  student_logins: ReportingMixpanelEvent[];
  story_opens: ReportingMixpanelEvent[];
  lessons_accessed: ReportingMixpanelEvent[];
  time_on_platform: ReportingMixpanelEvent[];
}

export const lookupNewDBStartDate = (event: string): Date => {
  const dateLibrary: {
    [key: string]: Date;
  } = {
    peekapakLogin: new Date('2023-08-10T00:00:00.000Z'),
    peekavilleLogin: new Date('2023-11-20T00:00:00.000Z'),
    platformEvents: new Date('2023-07-01T00:00:00.000Z'),
    journalMoodLog: new Date('2023-08-10T00:00:00.000Z'),
    startDate: new Date('2023-07-01T00:00:00.000Z'),
    completeDate: new Date('2023-11-20T00:00:00.000Z'),
    // Cutover date from which game progress, mood checkin, and journal events
    // are reliably available in Mixpanel and can replace DynamoDB queries.
    gameProgressMixpanel: new Date('2023-08-01T00:00:00.000Z'),
  };
  return dateLibrary[event];
};

export function getNextMonthFirstDay(date: number | Date) {
  const asDate = (() => {
    if (date instanceof Date) {
      return date;
    }

    return new Date(date);
  })();

  return getNextMonth(new Date(asDate.getFullYear(), asDate.getMonth(), 1));
}
export function getNextMonth(date: number | Date) {
  const asDate = (() => {
    if (date instanceof Date) {
      return date;
    }

    return new Date(date);
  })();

  const monthNumber = asDate.getMonth();
  const dateNumber = asDate.getDate();
  const yearNumber = asDate.getFullYear();

  if (monthNumber + 1 > 11) {
    return new Date(yearNumber + 1, 0, dateNumber);
  }

  return new Date(yearNumber, monthNumber + 1, dateNumber);
}

export function getDateLastSchoolYear() {
  const year = new Date(Date.now()).getFullYear();
  const month = new Date(Date.now()).getMonth();
  if (month < 6) {
    return `${year - 2}-07-01`;
  }

  return `${year - 1}-07-01`;
}

export function getDateBeginningOfSchoolYear() {
  const year = new Date(Date.now()).getFullYear();
  const month = new Date(Date.now()).getMonth();
  if (month < 6) {
    return `${year - 1}-07-01`;
  }

  return `${year}-07-01`;
}

export function getDateTwoWeeksAgo() {
  const twoWeeksAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000);
  return twoWeeksAgo.toISOString().split('T')[0];
}

export function getToday() {
  return new Date(Date.now()).toISOString().split('T')[0];
}

export function getDateFromTimestamp(timestamp: any) {
  const d = new Date(timestamp);
  return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`;
}

export function getListOfSchoolYears() {
  const currentSchoolYear = (() => {
    const year = new Date(Date.now()).getFullYear();
    const month = new Date(Date.now()).getMonth();
    if (month < 6) {
      return year - 1;
    }
    return year;
  })();

  const listOfYears = [
    {
      id: `${currentSchoolYear - 1}-${currentSchoolYear}`,
      name: `${currentSchoolYear - 1}-${currentSchoolYear}`,
      startingYear: `${currentSchoolYear - 1}`,
      endingYear: `${currentSchoolYear}`,
    },
    {
      id: `${currentSchoolYear}-${currentSchoolYear + 1}`,
      name: `${currentSchoolYear}-${currentSchoolYear + 1}`,
      startingYear: `${currentSchoolYear}`,
      endingYear: `${currentSchoolYear + 1}`,
    },
  ];
  return listOfYears;
}

export function getDefaultSchoolYear() {
  const listOfYears = getListOfSchoolYears();

  const initialValue = listOfYears[listOfYears.length - 1].id;
  return initialValue;
}

export function getDefaultSchoolYearMinusOne() {
  const listOfYears = getListOfSchoolYears();
  const initialValue = listOfYears[listOfYears.length - 2].id;
  return initialValue;
}

export function getTimestampFromDate(dateStr: string) {
  const date = new Date(dateStr);
  const timestamp = date.getTime();
  return timestamp;
}

export function getStudentsToLoginsMap(eventsArray: Array<{ name: string; distinct_id: string; userId: string }>) {
  const loginEvents = eventsArray.filter((e) =>
    ['user login', 'interaction end', EventName.LOGIN, EventName.INTERACTION_END].includes(e.name),
  );
  const loginMap = loginEvents.reduce(
    (acc, event) => {
      if (acc[event.userId || event.distinct_id]) {
        return {
          ...acc,
          [`${event.userId || event.distinct_id}`]: acc[event.userId || event.distinct_id] + 1,
        };
      } else {
        return { ...acc, [`${event.userId || event.distinct_id}`]: 1 };
      }
    },
    {} as Record<string, number>,
  );
  return loginMap;
}

export function getNumberOfActiveStudents(eventsArray: ReportingMixpanelEvent[]) {
  const loginMap = getStudentsToLoginsMap(eventsArray);
  return Object.keys(loginMap).length;
}

export function getTotalRow(tableData: any[]) {
  const totalNum = tableData
    .map((row) => {
      const numbers = Object.values(row);
      numbers.shift();
      return numbers;
    }, 0)
    .reduce((a: { [x: string]: any }, b: any[]) => {
      b.forEach((num, i) => {
        if (num != null && !isNaN(num)) {
          a[i] = (a[i] || 0) + Number(num);
        }
      });
      return a;
    }, []);
  while (totalNum.length > 0 && totalNum[totalNum.length - 1] === undefined) {
    totalNum.pop();
  }
  if (totalNum.length) totalNum.unshift('Total');
  return totalNum.length ? totalNum : null;
}

//
// NB:
// this function assumes that teacherClassroomMaps is ready
// and userList is ready
//
export function getCurrentYearTeachersData(
  userList: UserProfileType[],
  teacherClassroomMaps: TeacherClassroomMaps,
  legacyTeacherData: ReportingMixpanelEvent[],
  newTeacherData: ReportingMixpanelEvent[],
): CurrentYearTeacher[] {
  const teacherList: UserProfileType[] = [];

  const currentYearTeachers = teacherClassroomMaps.maps.filter((teacher) => teacher.classrooms.length > 0);
  currentYearTeachers.forEach((teacher) => {
    const foundUser = userList.find((user) => user.email === teacher.email);
    if (foundUser) teacherList.push(foundUser);
  });

  const newTeacherList = teacherList.map((teacherObj) => {
    const legacyDBEvents = legacyTeacherData.filter((event) => event.distinct_id === teacherObj.email);

    const newDBEvents = newTeacherData.filter((event) => event.userId === teacherObj.userId);

    const combinedEvents = [...legacyDBEvents, ...newDBEvents];

    const loginTimes = getLegacyEducatorLogins(legacyDBEvents) + getNewEducatorLogins(newDBEvents);

    /*
    const filteredTeacherObj = Object.values(teacherData).filter(
      (item) => `${item.key}` === teacherObj.email
    );
    const loginTimes = filteredTeacherObj.length
      ? filteredTeacherObj[0].value
      : 0;
      */

    return {
      ...teacherObj,
      loginTimes,
      events: combinedEvents,
    };
  });

  return newTeacherList;
}

export function getNumberOfOtherTeachers(
  teacherClassroomMaps: TeacherClassroomMaps,
  teacherClassroomMapsReady: boolean,
) {
  if (teacherClassroomMapsReady) {
    return teacherClassroomMaps.maps.reduce((acc, m) => acc + (m.classrooms.length === 0 ? 1 : 0), 0);
  }

  return 0;
}

//
// NB:
// "Other students" means students who have mixpanel events but are not Rostered
//
export function getOtherStudents(
  teacherStudentMap: Record<string, ReportStudent[]>,
  teacherStudentMapReady: boolean,
  mixpanelEvents: ReportingMixpanelEvent[],
  mixpanelEventsReady: boolean,
) {
  if (teacherStudentMapReady && mixpanelEventsReady) {
    const rosteredStudentList = Object.values(teacherStudentMap).flat();
    const studentEvents = mixpanelEvents.filter((event) => event.type === 'Student');
    const fromEventsStudentIdsSet = new Set(studentEvents.map((student) => student.userId));
    const fromEventsStudentIds = Array.from(fromEventsStudentIdsSet).filter(Boolean);

    const rosteredStudentIds = rosteredStudentList.map((student) => student.userId);

    const otherStudents = fromEventsStudentIds.filter((s) => !rosteredStudentIds.includes(s));
    console.debug(`otherStudents: ${otherStudents}`);
    return otherStudents;
  }
  return [];
}

export function getNumberOfOtherStudents(
  teacherStudentMap: Record<string, ReportStudent[]>,
  teacherStudentMapReady: boolean,
  mixpanelEvents: ReportingMixpanelEvent[],
  mixpanelEventsReady: boolean,
) {
  const others = getOtherStudents(teacherStudentMap, teacherStudentMapReady, mixpanelEvents, mixpanelEventsReady);
  return others.length;
}

export function getNumberOfRosteredTeachers(
  teacherClassroomMaps: TeacherClassroomMaps,
  teacherClassroomMapsReady: boolean,
) {
  if (teacherClassroomMapsReady)
    return teacherClassroomMaps.maps.reduce((acc, m) => acc + (m.classrooms.length > 0 ? 1 : 0), 0);

  return 0;
}

export function getNumberOfRosteredStudents(
  teacherStudentMap: Record<string, ReportStudent[]>,
  teacherStudentMapReady: boolean,
): number {
  if (teacherStudentMapReady) {
    const listOfTeachers = Object.keys(teacherStudentMap);
    const withStudents = listOfTeachers.filter((t) => teacherStudentMap[t].length > 0);
    const count = withStudents.reduce((acc, t) => {
      return acc + teacherStudentMap[t].length;
    }, 0);

    return count;
  }

  return 0;
}

export const secondaryUnits = [
  'welcome',
  'hswelcome',
  'identity',
  'hsidentity',
  'stress',
  'hsstress',
  'decision',
  'hsdecision',
  'belonging',
  'hsbelonging',
  'empathy',
  'hsempathy',
  'futureskills',
  'digitalliteracy',
];

export function getDistrictDataBySchool(data: ReportingMixpanelEvent[], schoolList: SchoolModel[]) {
  const isDataWithoutSchool = data.find((item) => item.schoolId === undefined);
  const dataBySchool = schoolList.map((school) =>
    school.combineIds
      ? data.filter((item) => school.combineIds?.includes(item.schoolId))
      : data.filter((item) => item.schoolId === school.id),
  );

  const dataIncludingNoSchool = isDataWithoutSchool
    ? [...dataBySchool, data.filter((item) => item.schoolId === undefined)]
    : dataBySchool;
  const parsedData = dataIncludingNoSchool.map((school, index) => {
    return {
      school_name: schoolList[index]?.name ? schoolList[index]?.name : 'Other',
      number_of_educators: getNumberOfEducators(school),
      educator_logins: getLegacyEducatorLogins(school) + getNewEducatorLogins(school),
      student_logins: getLegacyStudentLogins(school) + getNewStudentLogins(school),
      story_opens: getLegacyStoryOpens(school) + getNewStoryOpens(school),
      lessons_accessed: getLegacyLessonsAccessed(school) + getNewLessonsAccessed(school),
      time_on_platform: getLegacyTimeOnPlatform(school) + getNewTimeOnPlatform(school),
    };
  });
  return parsedData;
}

export function getNewDistrictDataBySchool(
  data: {
    number_of_educators: ReportingMixpanelEvent[];
    educator_logins: ReportingMixpanelEvent[];
    student_logins: ReportingMixpanelEvent[];
    story_opens: ReportingMixpanelEvent[];
    lessons_accessed: ReportingMixpanelEvent[];
    time_on_platform: ReportingMixpanelEvent[];
  },
  schoolList: SchoolModel[],
) {
  const schoolListWithOther = [
    ...schoolList,
    {
      name: 'Other',
      id: undefined,
    },
  ];

  const parsedData = schoolListWithOther.map((school, index) => {
    return {
      school_name: schoolList[index]?.name ? schoolList[index]?.name : 'Other',
      number_of_educators: new Set(
        filterBySchool(data.number_of_educators, school)
          .map((item) => item.userId || item.distinct_id)
          .filter(Boolean),
      ).size,
      educator_logins: filterBySchool(data.educator_logins, school).length,
      student_logins: filterBySchool(data.student_logins, school).length,
      story_opens: filterBySchool(data.story_opens, school).length,
      lessons_accessed: filterBySchool(data.lessons_accessed, school).length,
      time_on_platform: calculateTimeOnPlatform(filterBySchool(data.time_on_platform, school)),
    };
  });
  return parsedData;
}

export function getDistrictDataByType(
  legacyPeekavilleData: ReportingMixpanelEvent[],
  legacyPlatformData: ReportingMixpanelEvent[],
  newMixpanelData: ReportingMixpanelEvent[],
) {
  const combinedLegacyData = [...legacyPeekavilleData, ...legacyPlatformData];
  const dataObject = {
    number_of_educators: getEducatorEvents([...combinedLegacyData, ...newMixpanelData]),
    educator_logins: [
      ...getLegacyEducatorLoginEvents(legacyPlatformData),
      ...getNewEducatorLoginEvents(newMixpanelData),
    ],
    student_logins: [...getLegacyStudentLoginEvents(combinedLegacyData), ...getNewStudentLoginEvents(newMixpanelData)],
    story_opens: [...getLegacyStoryOpenEvents(combinedLegacyData), ...getNewStoryOpenEvents(newMixpanelData)],
    lessons_accessed: [
      ...getLegacyLessonsAccessedEvents(legacyPlatformData),
      ...getNewLessonsAccessedEvents(newMixpanelData),
    ],
    time_on_platform: [
      ...getLegacyTimeOnPlatformEvents(
        legacyPlatformData.filter((item) => ['Teacher', 'SchoolAdmin', 'DistrictAdmin', 'Student'].includes(item.type)),
      ),
      ...getLegacyTimeOnPlatformEvents(legacyPeekavilleData.filter((item) => item.type === 'Student')),
      ...getNewTimeOnPlatformEvents(
        newMixpanelData.filter((item) => ['Teacher', 'SchoolAdmin', 'DistrictAdmin', 'Student'].includes(item.type)),
      ),
    ],
  };
  return dataObject;
}

const filterBySchool = (array: ReportingMixpanelEvent[], school: Partial<SchoolModel>) => {
  const filteredArray = school.combineIds
    ? array.filter((item) => school.combineIds?.includes(item.schoolId))
    : array.filter((item) => item.schoolId === school.id);
  return filteredArray;
};

const calculateTimeOnPlatform = (array: ReportingMixpanelEvent[]) => {
  const totalSessionDuration = Math.ceil(
    array.reduce(
      (acc, curr) =>
        (acc = curr?.duration
          ? acc + (curr.duration > 21600 ? parseInt(curr.duration) / 1000 : parseInt(curr.duration))
          : acc),
      0,
    ) / 3600,
  );
  return totalSessionDuration;
};

const getEducatorEvents = (events: ReportingMixpanelEvent[]) => {
  return events.filter((item) => {
    if (!['Teacher', 'SchoolAdmin', 'DistrictAdmin'].includes(item.type)) return false;
    const isNew = item.name === EventName.LOGIN;
    const isLegacy =
      new Date(item.time) <= lookupNewDBStartDate('peekapakLogin') &&
      (item.name === 'User Login' || item.name === 'login' || item.name === 'User Reactivated');
    return isNew || isLegacy;
  });
};

export const filterMixpanelEventsByDate = (startDate: Date, endDate: Date, array: ReportingMixpanelEvent[]) => {
  const filteredArray = array.filter((item) => new Date(item.time) >= startDate && new Date(item.time) <= endDate);
  return filteredArray;
};

export const filterStudentObjectByDate = (
  startDate: Date,
  endDate: Date,
  studentObject: Record<string, Array<PeekavilleJournalEntry | Assignment>>,
) => {
  const filteredArray = Object.entries(studentObject).map(([k, v]) => {
    return [
      k,
      v.filter((item) => {
        if (item.time) {
          return new Date(item.time) >= startDate && new Date(item.time) <= endDate;
        } else if (item.createdAt) {
          return new Date(item.createdAt) >= startDate && new Date(item.createdAt) <= endDate;
        }
      }),
    ];
  });
  const filteredObject = Object.fromEntries(filteredArray);
  return filteredObject;
};

const getStudentEvents = (events: ReportingMixpanelEvent[]) => {
  const teacherEvents = events.filter((item) => item.type === 'Student');
  return teacherEvents;
};

const getLegacyEducatorLoginEvents = (events: ReportingMixpanelEvent[]) => {
  const oldDBLogins = events
    .filter((item) => new Date(item.time) <= lookupNewDBStartDate('peekapakLogin'))
    .filter((item) => item.name === 'User Login' || item.name === 'login');
  return oldDBLogins;
};

const getNewEducatorLoginEvents = (events: ReportingMixpanelEvent[]) => {
  const newDBLogins = events.filter(
    (item) => (item.type === 'Teacher' || item.type === 'SchoolAdmin') && item.name === EventName.LOGIN,
  );
  return newDBLogins;
};

const getLegacyStudentLoginEvents = (events: ReportingMixpanelEvent[]) => {
  const oldDBLogins = events
    .filter((item) => new Date(item.time) <= lookupNewDBStartDate('completeDate') && item.type === 'Student')
    .filter(
      (item) =>
        item.name === 'user login' ||
        item.name === 'User Login' ||
        item.name === 'interaction end' ||
        item.name === 'User Reactivated',
    );
  return oldDBLogins;
};

const getNewStudentLoginEvents = (events: ReportingMixpanelEvent[]) => {
  const newDBLogins = events.filter((item) => item.type === 'Student' && item.name === EventName.LOGIN);
  const newPeekaville = newDBLogins.filter(
    (item) => new Date(item.time) >= lookupNewDBStartDate('completeDate') && item.source === 'mypeekaville',
  );
  const newPlatform = newDBLogins.filter(
    (item) => new Date(item.time) >= lookupNewDBStartDate('peekapakLogin') && item.source === 'peekapak',
  );
  const combinedLogins = [...newPeekaville, ...newPlatform];
  return combinedLogins;
};

export const getLegacyStoryOpenEvents = (events: ReportingMixpanelEvent[]) => {
  const elementaryBookOpenRegexp = new RegExp(/^story.+pg1\s/gm);
  const oldDBStoryOpens = events
    .filter((item) => new Date(item.time) <= lookupNewDBStartDate('completeDate'))
    .filter(
      (item) =>
        item.name.includes('featuredstory') ||
        item.name === 'story-open' ||
        item.name.search(elementaryBookOpenRegexp) >= 0,
    );
  return oldDBStoryOpens;
};

export const getNewStoryOpenEvents = (events: ReportingMixpanelEvent[]) => {
  const newDBStoryOpens = events.filter((item) =>
    [
      EventName.JOURNAL_BOOK_OPEN,
      EventName.JOURNAL_ASSIGNMENT_STORY_OPEN,
      EventName.LESSONS_STORY_OPEN,
      EventName.LESSONS_BOOK_OPEN,
    ].includes(item.name),
  );
  return newDBStoryOpens;
};

const getLegacyLessonsAccessedEvents = (events: ReportingMixpanelEvent[]) => {
  const oldDBLessons = events
    .filter((item) => new Date(item.time) <= lookupNewDBStartDate('completeDate'))
    .filter(
      (item) =>
        (item.name.includes('lessonPlan') && item.name.includes('readAloud')) ||
        (item.name.includes('lessonPlan') && secondaryUnits.some((v) => item.name.includes(v))),
    );
  return oldDBLessons;
};

const getNewLessonsAccessedEvents = (events: ReportingMixpanelEvent[]) => {
  const newDBLessons = events.filter((item) => item.name === EventName.LESSONS_OPEN);
  return newDBLessons;
};

export const getLegacyTimeOnPlatformEvents = (events: ReportingMixpanelEvent[]) => {
  const oldDBSessionEnds = events
    .filter((item) => new Date(item.time) <= lookupNewDBStartDate('completeDate'))
    .filter((item) => item.name === 'interaction end' || item.name === 'Exit Website');
  // const totalSessionDuration = Math.floor(
  //   oldDBSessionEnds.reduce(
  //     (acc, curr) =>
  //       (acc = curr?.duration
  //         ? acc +
  //           (curr.duration > 21600
  //             ? parseInt(curr.duration) / 1000
  //             : parseInt(curr.duration))
  //         : acc),
  //     0
  //   ) / 3600
  // );
  return oldDBSessionEnds;
};

export const getNewTimeOnPlatformEvents = (events: ReportingMixpanelEvent[]) => {
  const newDBSessionEnds = events.filter(
    (item) => new Date(item.time) >= lookupNewDBStartDate('completeDate') && item.name === EventName.INTERACTION_END,
  );
  return newDBSessionEnds;
};

export const getNumberOfEducators = (events: ReportingMixpanelEvent[]) => {
  const educatorIds = events
    .filter((item) => {
      const isStaff = item.type === 'Teacher' || item.type === 'SchoolAdmin' || item.type === 'DistrictAdmin';
      if (!isStaff) return false;
      const isNew = item.name === EventName.LOGIN;
      const isLegacy =
        new Date(item.time) <= lookupNewDBStartDate('peekapakLogin') &&
        (item.name === 'User Login' || item.name === 'login' || item.name === 'User Reactivated');
      return isNew || isLegacy;
    })
    .map((item) => item.userId || item.distinct_id)
    .filter(Boolean);
  return new Set(educatorIds).size;
};

export const getNumberOfStudents = (events: ReportingMixpanelEvent[]) => {
  const studentIds = events
    .filter((item) => {
      if (item.type !== 'Student') return false;
      if (item.name === EventName.LOGIN) {
        const isNewPeekaville =
          new Date(item.time) >= lookupNewDBStartDate('completeDate') && item.source === 'mypeekaville';
        const isNewPlatform =
          new Date(item.time) >= lookupNewDBStartDate('peekapakLogin') && item.source === 'peekapak';
        if (isNewPeekaville || isNewPlatform) return true;
      }
      const CUTOFF = new Date('2023-10-01T00:00:00.000Z');
      const expiredNames = new Set(['user login', 'User Login', 'interaction end']);
      const legacyNames = new Set(['user login', 'User Login', 'interaction end', 'User Reactivated']);
      const isOldEnough = new Date(item.time) <= lookupNewDBStartDate('completeDate');
      if (isOldEnough && legacyNames.has(item.name)) {
        if (expiredNames.has(item.name) && new Date(item.time) >= CUTOFF) return false;
        return true;
      }
      return false;
    })
    .map((item) => item.userId || item.distinct_id)
    .filter(Boolean);
  return new Set(studentIds).size;
};

export const getLegacyEducatorLogins = (events: ReportingMixpanelEvent[]) => {
  const oldDBLogins = events
    .filter((item) => new Date(item.time) <= lookupNewDBStartDate('peekapakLogin'))
    .filter((item) => item.name === 'User Login' || item.name === 'login' || item.name === 'User Reactivated');
  return oldDBLogins.length;
};

export const getNewEducatorLogins = (events: ReportingMixpanelEvent[]) => {
  const newDBLogins = events.filter(
    (item) => ['Teacher', 'SchoolAdmin', 'DistrictAdmin'].includes(item.type) && item.name === EventName.LOGIN,
  );
  return newDBLogins.length;
};

export const getLegacyStudentLogins = (events: ReportingMixpanelEvent[]) => {
  const CUTOFF = new Date('2023-10-01T00:00:00.000Z');
  const legacyNames = new Set(['user login', 'User Login', 'interaction end', 'User Reactivated']);
  const expiredNames = new Set(['user login', 'User Login', 'interaction end']);
  const oldDBLogins = events
    .filter((item) => new Date(item.time) <= lookupNewDBStartDate('completeDate') && item.type === 'Student')
    .filter((item) => {
      if (expiredNames.has(item.name) && new Date(item.time) >= CUTOFF) return false;
      return legacyNames.has(item.name);
    });
  return oldDBLogins.length;
};

export const getNewStudentLogins = (events: ReportingMixpanelEvent[]) => {
  const newDBLogins = events.filter((item) => item.type === 'Student' && item.name === EventName.LOGIN);
  const newPeekaville = newDBLogins.filter(
    (item) => new Date(item.time) >= lookupNewDBStartDate('completeDate') && item.source === 'mypeekaville',
  );
  const newPlatform = newDBLogins.filter(
    (item) => new Date(item.time) >= lookupNewDBStartDate('peekapakLogin') && item.source === 'peekapak',
  );
  const combinedLogins = [...newPeekaville, ...newPlatform];
  return combinedLogins.length;
};

export const getLegacyStoryOpens = (events: ReportingMixpanelEvent[]) => {
  const elementaryBookOpenRegexp = new RegExp(/^story.+pg1\s/gm);
  const oldDBStoryOpens = events
    .filter((item) => new Date(item.time) <= lookupNewDBStartDate('completeDate'))
    .filter(
      (item) =>
        item.name.includes('featuredstory') ||
        item.name === 'story-open' ||
        item.name.search(elementaryBookOpenRegexp) >= 0,
    );
  return oldDBStoryOpens.length;
};

export const getNewStoryOpens = (events: ReportingMixpanelEvent[]) => {
  const newDBStoryOpens = events.filter((item) =>
    [
      EventName.JOURNAL_BOOK_OPEN,
      EventName.JOURNAL_ASSIGNMENT_STORY_OPEN,
      EventName.LESSONS_STORY_OPEN,
      EventName.LESSONS_BOOK_OPEN,
    ].includes(item.name),
  );
  return newDBStoryOpens.length;
};

export const getLegacyLessonsAccessed = (events: ReportingMixpanelEvent[]) => {
  const oldDBLessons = events
    .filter((item) => new Date(item.time) <= lookupNewDBStartDate('completeDate'))
    .filter(
      (item) =>
        (item.name.includes('lessonPlan') && item.name.includes('readAloud')) ||
        (item.name.includes('lessonPlan') && secondaryUnits.some((v) => item.name.includes(v))),
    );
  return oldDBLessons.length;
};

export const getNewLessonsAccessed = (events: ReportingMixpanelEvent[]) => {
  const newDBLessons = events.filter((item) => item.name === 'lessons-open');
  return newDBLessons.length;
};

export const getLegacyTimeOnPlatform = (events: ReportingMixpanelEvent[]) => {
  const oldDBSessionEnds = events
    .filter((item) => new Date(item.time) <= lookupNewDBStartDate('completeDate'))
    .filter((item) => item.name === 'interaction end' || item.name === 'Exit Website');
  const totalSessionDuration = Math.ceil(
    oldDBSessionEnds.reduce(
      (acc, curr) =>
        (acc = curr?.duration
          ? acc + (curr.duration > 21600 ? parseInt(curr.duration) / 1000 : parseInt(curr.duration))
          : acc),
      0,
    ) / 3600,
  );
  return totalSessionDuration;
};

export const getNewTimeOnPlatform = (events: ReportingMixpanelEvent[]) => {
  const newDBSessionEnds = events
    .filter((item) => new Date(item.time) >= lookupNewDBStartDate('completeDate'))
    .filter((item) => item.name === 'interaction-end');
  const totalSessionDuration = Math.ceil(
    newDBSessionEnds.reduce(
      (acc, curr) =>
        (acc = curr?.duration
          ? acc + (curr.duration > 21600 ? parseInt(curr.duration) / 1000 : parseInt(curr.duration))
          : acc),
      0,
    ) / 3600,
  );
  return totalSessionDuration;
};

const getTimePeriods = (
  startDate: Date,
  endDate: Date,
  minBarWidth: number,
  gapWidth: number,
  containerWidth: number,
  forcedNumberOfDaysInCount?: number,
) => {
  if (forcedNumberOfDaysInCount === 30) {
    return getMonthPeriods(startDate, endDate);
  }
  if (forcedNumberOfDaysInCount) {
    return getPeriods(startDate, endDate, forcedNumberOfDaysInCount);
  }

  interface Span {
    // how many of each span, e.g. 73 days or 11 weeks or 3 months
    count: number;
    // how many days in each count of span, 1 day, 7 days, 30 days
    numberOfDaysInCount: number;
  }

  const daysApart = (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24);

  const spans: [Span, Span, Span, Span, Span] = (() => {
    const days = daysApart;
    const weeks = Math.ceil(days / 7);
    const months = Math.ceil(weeks / 4);
    const quarters = Math.ceil(months / 3);
    const years = Math.ceil(months / 12);

    return [
      { count: days, numberOfDaysInCount: 1 },
      { count: weeks, numberOfDaysInCount: 7 },
      { count: months, numberOfDaysInCount: 30 },
      { count: quarters, numberOfDaysInCount: 90 },
      { count: years, numberOfDaysInCount: 365 },
    ];
  })();

  for (const span of spans) {
    const { count, numberOfDaysInCount } = span;
    const totalWidth = count * minBarWidth + (count - 1) * gapWidth;
    if (totalWidth <= containerWidth) {
      logDebugLevel('getTimePeriods done');
      return getPeriods(startDate, endDate, numberOfDaysInCount);
    }
  }

  throw new Error('Chart width is too small to fit any time periods for data display');

  // TODO: handle timezone offset, since data is recorded in GMT
  function getMonthPeriods(startDate: Date, endDate: Date) {
    const timePeriods: { from: Date; to: Date }[] = [];
    let currentDate = new Date(endDate);
    currentDate.setHours(23, 59, 59, 999);

    while (currentDate >= startDate) {
      const to = new Date(currentDate);
      to.setHours(23, 59, 59, 999);

      const from = new Date(currentDate);
      from.setDate(1);
      from.setHours(0, 0, 0, 0);

      if (from < startDate) {
        timePeriods.push({ from: startDate, to });
        break;
      }

      timePeriods.push({ from, to });

      currentDate = new Date(from);
      currentDate.setDate(0);
      currentDate.setHours(23, 59, 59, 999);
    }

    return timePeriods;
  }

  function getPeriods(startDate: Date, endDate: Date, numberOfDaysInCount: number) {
    const timePeriods = [];
    let currentDate = endDate;
    let i = 0;
    while (currentDate >= startDate) {
      // Set 'to' to end of current date (23:59:59.999)
      const to = new Date(currentDate);
      to.setHours(23, 59, 59, 999);

      // Set 'from' to start of day, then subtract the bucket size
      const from = new Date(currentDate);
      from.setHours(0, 0, 0, 0);
      from.setDate(from.getDate() - numberOfDaysInCount);

      // Add 1 day to 'from' so buckets don't overlap
      // This means bucket goes from (from + 1 day) to (to)
      from.setDate(from.getDate() + 1);

      if (from <= startDate) {
        from.setDate(startDate.getDate());
        from.setMonth(startDate.getMonth());
        from.setFullYear(startDate.getFullYear());
        from.setHours(0, 0, 0, 0);
        timePeriods.push({ from, to });
        break;
      }
      timePeriods.push({ from, to });

      // Set currentDate to the day before 'from' for next iteration
      currentDate = new Date(from);
      currentDate.setDate(currentDate.getDate() - 1);
      i++;
    }
    return timePeriods;
  }
};

const filterByTime = (data: ReportingMixpanelEvent[], timePeriod: { from: Date; to: Date }) => {
  return data.filter((item) => item.time >= timePeriod.from.getTime() && item.time <= timePeriod.to.getTime());
};

export function getChartData(params: ChartDataCalculationParams) {
  const { data, startDate, endDate, schoolList, minBarWidth, gapWidth, containerWidth, forcedNumberOfDaysInCount } =
    params;

  const timePeriods = getTimePeriods(
    startDate,
    endDate,
    minBarWidth,
    gapWidth,
    containerWidth,
    forcedNumberOfDaysInCount,
  );

  const dataByTimePeriod = timePeriods.map((timePeriod) => {
    const newDataObject = {
      number_of_educators: filterByTime(data.number_of_educators, timePeriod),
      educator_logins: filterByTime(data.educator_logins, timePeriod),
      student_logins: filterByTime(data.student_logins, timePeriod),
      story_opens: filterByTime(data.story_opens, timePeriod),
      lessons_accessed: filterByTime(data.lessons_accessed, timePeriod),
      time_on_platform: filterByTime(data.time_on_platform, timePeriod),
    };
    return { timePeriod: timePeriod, data: newDataObject };
  });

  const dataByTimeAndSchool = dataByTimePeriod?.map((timeSpan, index) => {
    const dataBySchool = getNewDistrictDataBySchool(timeSpan.data, schoolList);
    return {
      timePeriod: timeSpan.timePeriod,
      data: dataBySchool,
      index: index,
    };
  });
  return dataByTimeAndSchool.reverse();
}

export function parseChartData(
  data: { timePeriod: { from: Date; to: Date }; data: DistrictDataBySchool }[],
  eventToChart: string,
  topFiveSchools: string[],
) {
  const chartData = data.map((timeSpan) => {
    const timePeriod = timeSpan.timePeriod;
    const dataBySchool = timeSpan.data;
    const topFiveSchoolData = topFiveSchools.reduce((acc, curr) => {
      const schoolData = dataBySchool.find((item) => item.school_name === curr);
      return {
        ...acc,
        [curr]: schoolData ? schoolData[eventToChart as string as keyof Omit<DistrictData, 'school_name'>] : 0,
      };
    }, {} as DistrictData);
    const otherData = dataBySchool.filter((item) => !topFiveSchools.includes(item.school_name));
    const otherDataTotal = otherData.reduce(
      (acc, curr) => acc + curr[eventToChart as string as keyof Omit<DistrictData, 'school_name'>],
      0,
    );
    const otherDataFormatted = { 'All Other Schools': otherDataTotal };
    const timePeriodData = {
      name: `${timePeriod.from.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        //year: 'numeric',
      })}`,
      ...topFiveSchoolData,
      ...otherDataFormatted,
    };
    return timePeriodData;
  });

  return chartData;
}

export function getTopFiveSchools(data: DistrictDataBySchool, eventToChart: string) {
  const otherAndEmptyRemoved = data.filter(
    (item) => item.school_name !== 'Other' && item[eventToChart as keyof DistrictData] !== 0,
  );
  const sortedData = otherAndEmptyRemoved.sort(
    (a, b) => Number(b[eventToChart as keyof DistrictData]) - Number(a[eventToChart as keyof DistrictData]),
  );
  const topFive = sortedData.slice(0, 5);
  const topFiveNames = topFive.map((item) => item.school_name);
  return topFiveNames;
}

const getSchoolChartDataByUserType = (data: ReportingMixpanelEvent[], types: string[]) => {
  const dataByType = types.map((type) => {
    const dataByType = data.filter((item) =>
      type === 'Educators'
        ? item.type === 'Teacher' || item.type === 'SchoolAdmin' || item.type === 'DistrictAdmin'
        : item.type === 'Student',
    );
    return {
      user_type: type,
      active_users: type === 'Educators' ? getNumberOfEducators(dataByType) : getNumberOfStudents(dataByType),
      user_logins:
        type === 'Educators'
          ? getLegacyEducatorLogins(dataByType) + getNewEducatorLogins(dataByType)
          : getLegacyStudentLogins(dataByType) + getNewStudentLogins(dataByType),
      lessons_accessed:
        type === 'Educators' ? getLegacyLessonsAccessed(dataByType) + getNewLessonsAccessed(dataByType) : 0,
      story_opens: getLegacyStoryOpens(dataByType) + getNewStoryOpens(dataByType),
      time_on_platform: getLegacyTimeOnPlatform(dataByType) + getNewTimeOnPlatform(dataByType),
    };
  });
  return dataByType;
};

export function getSchoolChartData(
  legacyPeekavilleData: ReportingMixpanelEvent[],
  legacyPlatformData: ReportingMixpanelEvent[],
  newMixpanelData: ReportingMixpanelEvent[],
  otherParams: SchoolChartDataCalculationParams,
) {
  logDebugLevel('getSchoolChartData start');
  const { startDate, endDate, minBarWidth, gapWidth, containerWidth, forcedNumberOfDaysInCount } = otherParams;
  const timePeriods = getTimePeriods(
    startDate,
    endDate,
    minBarWidth,
    gapWidth,
    containerWidth,
    forcedNumberOfDaysInCount,
  );

  const combinedEvents = [...legacyPeekavilleData, ...legacyPlatformData, ...newMixpanelData];

  const dataByTimePeriod = timePeriods.map((timePeriod) => {
    const newData = filterByTime(combinedEvents, timePeriod);
    return { timePeriod: timePeriod, data: newData };
  });

  const dataByTimeAndType = dataByTimePeriod?.map((timeSpan, index) => {
    const dataByUserType = getSchoolChartDataByUserType(timeSpan.data, ['Educators', 'Students']);

    return {
      timePeriod: timeSpan.timePeriod,
      data: dataByUserType,
      index: index,
    };
  });
  logDebugLevel('getSchoolChartData ', dataByTimeAndType);
  return dataByTimeAndType.reverse();
}

export const getChartFill = (index: number) => {
  const colors = {
    0: '#012A73',
    1: '#00Afd8',
    2: '#69Be28',
    3: '#ffd100',
    4: '#ff6319',
    5: '#7a7a7a',
  };
  return colors[index as keyof typeof colors];
};

export const getSchoolChartFill = (index: number) => {
  const colors = {
    0: '#012A73',
    1: '#00Afd8',
  };
  return colors[index as keyof typeof colors];
};

export function parseSchoolChartData(
  data: { timePeriod: { from: Date; to: Date }; data: DistrictDataBySchool }[],
  eventToChart: string,
) {
  const chartData = data.map((timeSpan) => {
    const timePeriod = timeSpan.timePeriod;
    const dataByType = timeSpan.data;
    const formattedData = ['Educators', 'Students'].reduce((acc, curr) => {
      const typeData = dataByType.find((item) => item.user_type === curr);
      return {
        ...acc,
        [curr]: typeData ? typeData[eventToChart as string as keyof Omit<DistrictData, 'user_type'>] : 0,
      };
    }, {} as DistrictData);
    const timePeriodData = {
      name: `${timePeriod.from.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        // year: 'numeric',
      })}`,
      fromDate: timePeriod.from.getTime(),
      toDate: timePeriod.to.getTime(),
      ...formattedData,
    };
    return timePeriodData;
  });

  return chartData;
}

export const getStartOfSchoolYear = () => {
  const startOfSchoolYear = new Date();
  startOfSchoolYear.setFullYear(startOfSchoolYear.getFullYear() - (startOfSchoolYear.getMonth() < 6 ? 1 : 0));
  startOfSchoolYear.setMonth(6);
  startOfSchoolYear.setDate(1);
  startOfSchoolYear.setHours(0, 0, 0, 0);
  return startOfSchoolYear;
};

export const getMixpanelDatabaseByDate = (startDate: Date, endDate: Date) => {
  if (startDate > lookupNewDBStartDate('completeDate')) return 'newDB';
  if (endDate < lookupNewDBStartDate('startDate')) return 'legacyDB';
  else return 'mixed';
};

export function getUpdatedDistrictUnifiedScript(startDate: string, endDate: string, districtId: string) {
  const districtFilter = `event.properties['district'] === '${districtId}'`;

  return noWhiteSpace` 
    function main() {
        return Events({
          from_date: '${startDate}',
          to_date: '${endDate}',
        })
          .filter(function(event) {
            return (
              ${districtFilter}
            && (event.properties['Email'] ? !event.properties['Email'].includes('windsongs') : true)
          );
      })
      .map(function(item) {
        const { distinct_id, name, properties, time } = item;

        const duration = properties['duration'];

        const toReturn = {
          time,
          duration: duration === undefined ? 0 : duration,
          distinct_id,
          name,
          lesson: properties['lesson'],
          source: properties['source'],
          userId: properties['userId'],
          classroomId: properties['classroomId'],
          schoolId: properties['schoolId'],
          type: properties['userType'],
        };
        return toReturn;
      });
  }
  `;
}

export function getUpdatedSchoolUnifiedScript(startDate: string, endDate: string, schoolIds: string | string[]) {
  const schoolFilter = (() => {
    if (typeof schoolIds === 'string') {
      return `event.properties['schoolId'] === '${schoolIds}'`;
    }

    let acc = `event.properties['schoolId'] === '${schoolIds[0]}'`;
    let i = 1;

    while (i < schoolIds.length) {
      acc += ` || event.properties['schoolId'] === '${schoolIds[i]}'`;
      i++;
    }
    return acc;
  })();
  return noWhiteSpace`
  function main() {
    return Events({
      from_date: '${startDate}',
      to_date: '${endDate}',
    })
      .filter(function(event) {
        return (
          ${schoolFilter}
            && (event.properties['Email'] ? !event.properties['Email'].includes('windsongs') : true)
          );
  })
        .map(function(item) {
    const { distinct_id, name, properties, time, source } = item;

    const toReturn = {
      time,
      duration: properties['duration'],
      distinct_id,
      name,
      lesson: properties['lesson'],
      mood: properties['mood'],
      source: properties['source'],
      userId: properties['userId'],
      classroomId: properties['classroomId'],
      schoolId: properties['schoolId'],
      type: properties['userType'],
      grade: properties['grade'],
      quest: properties['quest'],
      gameId: properties['gameId'],
      book: properties['book'],
      unit: properties['unit'],
      story: properties['story'],
      score: properties['score'],
      completionPercent: properties['completionPercent'],
      readingLevel: properties['readingLevel'],
    };
    return toReturn;
  });
    }
`;
}

export function getLegacyDistrictPlatformScript(startDate: string, endDate: string, districtId: string) {
  const districtFilter = `event.properties['District'] === '${districtId}'`;

  return noWhiteSpace`
function main() {
  return Events({
    from_date: '${startDate}',
    to_date: '${endDate}',
  })
    .filter(function(event) {
      return (
        ${districtFilter}
            && (event.properties['Email'] ? !event.properties['Email'].includes('windsongs') : true)
          );
})
        .map(function(item) {
  const { distinct_id, name, properties, time } = item;

  const toReturn = {
    duration: properties['Time Spent'],
    schoolId: properties['School ID'],
    time,
    distinct_id,
    name,
    type: properties['Type'],
  };

  return toReturn;
});
    }
`;
}

export function getLegacyDistrictPeekavilleScript(startDate: string, endDate: string, districtId: string) {
  const districtFilter = `event.properties['district'] === '${districtId}'`;

  return noWhiteSpace`
function main() {
  return Events({
    from_date: '${startDate}',
    to_date: '${endDate}',
  })
    .filter(function(event) {
      return (
        ${districtFilter}
          );
})
        .map(function(item) {
  const { distinct_id, name, properties, time } = item;

  const {
    classroomId,
    district,
    schoolId,
    type,
    eventName,
    userId,
  } = properties;

  const duration = properties['$duration'];

  const toReturn = {
    time,
    distinct_id,
    name,
    duration: duration === undefined ? 0 : duration,
    schoolId,
    userId,
    type,
  };

  if (eventName) {
    toReturn.eventName = eventName;
  }

  return toReturn;
});
    }
`;
}

export function getLegacySchoolPlatformScript(startDate: string, endDate: string, schoolIds: string | string[]) {
  const endDateAsDate = new Date(endDate);
  const queryEndDate = (() => {
    if (endDateAsDate > lookupNewDBStartDate('platformEvents')) {
      return lookupNewDBStartDate('platformEvents').toISOString().split('T')[0];
    }
    return endDate;
  })();

  const schoolFilter = (() => {
    if (typeof schoolIds === 'string') {
      return `event.properties['School ID'] === '${schoolIds}'`;
    }

    let acc = `event.properties['School ID'] === '${schoolIds[0]}'`;
    let i = 1;

    while (i < schoolIds.length) {
      acc += ` || event.properties['School ID'] === '${schoolIds[i]}'`;
      i++;
    }
    return acc;
  })();
  return noWhiteSpace`
function main() {
  return Events({
    from_date: '${startDate}',
    to_date: '${queryEndDate}',
  })
    .filter(function(event) {
      return (
        ${schoolFilter}
            && (event.properties['Email'] ? !event.properties['Email'].includes('windsongs') : true)
          );
})
        .map(function(item) {
  const { distinct_id, name, properties, time } = item;

  const toReturn = {
    time,
    duration: properties['Time Spent'],
    distinct_id,
    name,
    userId: properties['User ID'],
    classroomId: properties['Classroom ID'],
    email: properties['Email'],
    type: properties['Type'],
  };

  return toReturn;
});
    }
`;
}

export function getLegacySchoolPeekavilleScript(startDate: string, endDate: string, schoolIds: string | string[]) {
  const schoolFilter = (() => {
    if (typeof schoolIds === 'string') {
      return `event.properties['schoolId'] === '${schoolIds}'`;
    }

    let acc = `event.properties['schoolId'] === '${schoolIds[0]}'`;
    let i = 1;

    while (i < schoolIds.length) {
      acc += ` || event.properties['schoolId'] === '${schoolIds[i]}'`;
      i++;
    }
    return acc;
  })();
  return noWhiteSpace`
function main() {
  return Events({
    from_date: '${startDate}',
    to_date: '${endDate}',
  })
    .filter(function(event) {
      return (
        ${schoolFilter}
              );
})
            .map(function(item) {
  const { distinct_id, name, properties, time } = item;

  const {
    classroomId,
    district,
    schoolId,
    type,
    eventName,
    userId,
  } = properties;

  const duration = properties['$duration'];

  const toReturn = {
    time,
    distinct_id,
    name,
    duration: duration === undefined ? 0 : duration,
    schoolId,
    userId,
    type,
  };

  if (eventName) {
    toReturn.eventName = eventName;
  }

  return toReturn;
});
        }
`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Mixpanel migration: activity ID normalisation helpers
// ─────────────────────────────────────────────────────────────────────────────

// Maps lowercase activity key → canonical camelCase key from Associations.json.
// Used to reconcile Mixpanel event values (sent as all-lowercase by myPeekaville)
// with the camelCase keys that compileUnitData looks up in Associations.json.
//
// Quest goal IDs (e.g. helpZoeyApologize, talkToLeo) are NOT in Associations.json
// and will not appear in this map — they fall through and are correctly skipped
// by compileUnitData's association guard.
import associationData from '../ClassroomDashboard/Associations.json';
const associationKeyMap: Record<string, string> = Object.fromEntries(
  Object.keys((associationData as { associations: Record<string, unknown> }).associations).map((k) => [
    k.toLowerCase(),
    k,
  ]),
);

/**
 * Resolves a raw Mixpanel activity ID to the canonical key used in
 * Associations.json, handling:
 *   - book events: Mixpanel sends bare integer (e.g. 1), Associations needs "book_1"
 *   - quest/gameId events: Mixpanel sends lowercase, Associations uses camelCase
 *
 * Returns the resolved key, or the raw value if no match found (will be skipped
 * downstream by compileUnitData).
 */
function resolveActivityId(event: ReportingMixpanelEvent): string {
  const rawBook = (event as any).book;
  const isBookEvent = event.name === 'journal-book-complete' || event.name === 'journal-reading-comprehension-complete';

  // For book events, construct the "book_N" key before the map lookup
  const rawId: string =
    (event as any).quest ||
    (event as any).gameId ||
    (isBookEvent && rawBook !== undefined && rawBook !== null ? `book_${rawBook}` : null) ||
    '';

  // Normalise to canonical camelCase key; fall back to rawId if not found
  return associationKeyMap[rawId.toLowerCase()] ?? rawId;
}

// ─────────────────────────────────────────────────────────────────────────────
// Mixpanel migration: classroom-scoped JQL scripts
// ─────────────────────────────────────────────────────────────────────────────

/**
 * JQL script that fetches journal-mood-log events for a single classroom.
 * Used by MoodboardComponent (elementary) and MoodboardMiddleSchool as a
 * replacement for the per-student DynamoDB getClassroomFeelingsCheckins /
 * getMiddleSchoolJournals calls.
 *
 * The from_date is floored to gameProgressMixpanel cutover date at call site.
 */
export function getClassroomMoodScript(classroomId: string, fromDate: string, toDate: string): string {
  return noWhiteSpace`
    function main() {
      return Events({
        from_date: '${fromDate}',
        to_date: '${toDate}',
      })
        .filter(function(event) {
          return (
            event.properties['classroomId'] === '${classroomId}' &&
            event.name === 'journal-mood-log'
          );
        })
        .map(function(item) {
          const { properties, time } = item;
          return {
            time,
            userId:      properties['userId'],
            classroomId: properties['classroomId'],
            mood:        properties['mood'],
            teacherRequested: properties['teacherRequested'],
            grade:       properties['grade'],
            reasons:     properties['reasons'],
          };
        });
    }
  `;
}

/**
 * JQL script that fetches all game progress completion events for a single
 * classroom. Used by PeekavilleDataHelpers.getClassGameData as a replacement
 * for the DynamoDB getClassroomGameProgress call.
 *
 * Events returned: journal-quest-complete, journal-graded-mini-game-complete,
 * journal-book-complete, journal-reading-comprehension-complete.
 *
 * The from_date is floored to gameProgressMixpanel cutover date at call site.
 */
export function getClassroomGameProgressScript(classroomId: string, fromDate: string, toDate: string): string {
  return noWhiteSpace`
    function main() {
      return Events({
        from_date: '${fromDate}',
        to_date: '${toDate}',
      })
        .filter(function(event) {
          return (
            event.properties['classroomId'] === '${classroomId}' &&
            (
              event.name === 'journal-quest-complete' ||
              event.name === 'journal-graded-mini-game-complete' ||
              event.name === 'journal-book-complete' ||
              event.name === 'journal-reading-comprehension-complete'
            )
          );
        })
        .map(function(item) {
          const { properties, time, name } = item;
          return {
            time,
            name,
            userId:            properties['userId'],
            classroomId:       properties['classroomId'],
            schoolId:          properties['schoolId'],
            quest:             properties['quest'],
            gameId:            properties['gameId'],
            book:              properties['book'],
            unit:              properties['unit'],
            score:             properties['score'],
            completionPercent: properties['completionPercent'],
            readingLevel:      properties['readingLevel'],
            rightNum:          properties['rightNum'],
            wrongNum:          properties['wrongNum'],
            numRight:          properties['numRight'],
            numWrong:          properties['numWrong'],
            levelPlayed:       properties['levelPlayed'],
          };
        });
    }
  `;
}

// ─────────────────────────────────────────────────────────────────────────────
// Mixpanel migration: aggregation / transformation helpers
// ─────────────────────────────────────────────────────────────────────────────

const GAME_PROGRESS_EVENT_NAMES = new Set([
  'journal-quest-complete',
  'journal-graded-mini-game-complete',
  'journal-book-complete',
  'journal-reading-comprehension-complete',
]);

/**
 * Returns the effective query start date, floored to the Mixpanel game progress
 * cutover date (Aug 1 2023) so we never ask Mixpanel for data it doesn't have.
 */
export function getGameProgressQueryStartDate(requestedStart: string): string {
  const cutover = lookupNewDBStartDate('gameProgressMixpanel');
  const requested = new Date(requestedStart);
  return (requested < cutover ? cutover : requested).toISOString().split('T')[0];
}

/**
 * Transforms an array of Mixpanel game-progress events (already fetched as
 * part of newMixpanelEvents via getUpdatedSchoolUnifiedScript) into the
 * gameProgressMap shape expected by StudentsElementary:
 *
 *   Record<userId, { completionPercent, displayName, id, lastDateOpened,
 *                    readingLevel, timeSpent, type }[]>
 *
 * Each entry in the array represents one completed activity for that student.
 */
export function aggregateGameProgressByStudent(events: ReportingMixpanelEvent[]): Record<
  string,
  {
    completionPercent: number;
    displayName: string;
    id: string;
    lastDateOpened: number;
    readingLevel: number;
    timeSpent: number;
    type: string;
  }[]
> {
  const result: Record<
    string,
    {
      completionPercent: number;
      displayName: string;
      id: string;
      lastDateOpened: number;
      readingLevel: number;
      timeSpent: number;
      type: string;
    }[]
  > = {};

  const gameEvents = events.filter((e) => GAME_PROGRESS_EVENT_NAMES.has(e.name));

  for (const event of gameEvents) {
    const userId = event.userId;
    if (!userId) continue;

    if (!result[userId]) result[userId] = [];

    const activityId: string = resolveActivityId(event);
    const activityType = (() => {
      if (event.name === 'journal-quest-complete') return 'quest';
      if (event.name === 'journal-graded-mini-game-complete') return 'mini-game';
      if (event.name === 'journal-book-complete') return 'book';
      if (event.name === 'journal-reading-comprehension-complete') return 'book';
      return 'unknown';
    })();

    // Avoid duplicate entries: keep the most recent completion for each activity
    const existingIndex = result[userId].findIndex((e) => e.id === activityId);
    const entry = {
      completionPercent: Number((event as any).completionPercent ?? 100),
      displayName: activityId,
      id: activityId,
      lastDateOpened: typeof event.time === 'number' ? event.time : new Date(event.time).getTime(),
      readingLevel: Number((event as any).readingLevel ?? 0),
      timeSpent: 0,
      type: activityType,
    };

    if (existingIndex === -1) {
      result[userId].push(entry);
    } else if (entry.lastDateOpened > result[userId][existingIndex].lastDateOpened) {
      result[userId][existingIndex] = entry;
    }
  }

  return result;
}

/**
 * Transforms Mixpanel game progress events (from getClassroomGameProgressScript)
 * into the StudentProgressData[] shape expected by PeekavilleProgressReport /
 * compileStudentData. Struggle and comprehension data are embedded directly
 * from the respective event types, eliminating per-student DynamoDB calls.
 *
 * Result shape mirrors the output of getClassroomGameProgress (DynamoDB).
 */
export function aggregateClassroomProgressByStudent(events: ReportingMixpanelEvent[]): StudentProgressData[] {
  // Group events by userId
  const byUser: Record<string, ReportingMixpanelEvent[]> = {};
  for (const event of events) {
    const userId = event.userId;
    if (!userId) continue;
    if (!byUser[userId]) byUser[userId] = [];
    byUser[userId].push(event);
  }

  return Object.entries(byUser).map(([userId, userEvents]) => {
    const progress: StudentProgressData['gameData']['progress'] = userEvents.map((event) => {
      const activityId: string = resolveActivityId(event);
      const activityType = (() => {
        if (event.name === 'journal-quest-complete') return 'quest';
        if (event.name === 'journal-graded-mini-game-complete') return 'mini-game';
        if (event.name === 'journal-book-complete') return 'book';
        if (event.name === 'journal-reading-comprehension-complete') return 'book';
        return 'unknown';
      })();

      const struggleData =
        event.name === 'journal-graded-mini-game-complete'
          ? [
              {
                userId,
                createdAt: typeof event.time === 'number' ? event.time : new Date(event.time).getTime(),
                classroomId: (event as any).classroomId ?? '',
                type: 'struggle' as const,
                dataVersion: 1,
                gameId: (event as any).gameId ?? '',
                levelPlayed: (event as any).levelPlayed ?? 0,
                modifiedAt: typeof event.time === 'number' ? event.time : new Date(event.time).getTime(),
                rightNum: (event as any).rightNum ?? 0,
                wrongNum: (event as any).wrongNum ?? 0,
              },
            ]
          : undefined;

      const comprehensionData =
        event.name === 'journal-reading-comprehension-complete'
          ? [
              {
                userId,
                createdAt: typeof event.time === 'number' ? event.time : new Date(event.time).getTime(),
                classroomId: (event as any).classroomId ?? '',
                type: 'comprehension' as const,
                book: Number((event as any).book ?? 0),
                level: Number((event as any).readingLevel ?? 0),
                numRight: (event as any).numRight ?? 0,
                numWrong: (event as any).numWrong ?? 0,
                time: typeof event.time === 'number' ? event.time : new Date(event.time).getTime(),
                individualQuestionsData: [],
              },
            ]
          : undefined;

      return {
        id: activityId,
        type: activityType,
        displayName: activityId,
        completionPercent: String((event as any).completionPercent ?? 100),
        lastDateOpened: typeof event.time === 'number' ? event.time : new Date(event.time).getTime(),
        readingLevel: Number((event as any).readingLevel ?? 0),
        timeSpent: 0,
        ...(struggleData ? { struggleData } : {}),
        ...(comprehensionData ? { struggleData: comprehensionData } : {}),
      };
    });

    return {
      firstName: '',
      lastName: '',
      userId,
      userIdHash: userId,
      gameData: {
        berries: 0,
        dailyEmotions: [],
        progress,
        units: [],
      },
    };
  });
}

/**
 * Transforms Mixpanel journal-mood-log events (from getClassroomMoodScript)
 * into the StudentJournalData[] shape consumed by MoodboardComponent
 * (elementary). Mood is already a string from Mixpanel — no numeric conversion.
 *
 * Shape: { userIdHash, firstName, lastName, journals: Record<string, { createdAt, feeling, ... }> }[]
 */
export function transformMoodEventsToElementaryJournalData(events: ReportingMixpanelEvent[]): {
  userIdHash: string;
  firstName: string;
  lastName: string;
  journals: Record<string, { createdAt: number; feeling: string; userId: string; classroomId: string }>;
}[] {
  const byUser: Record<string, { createdAt: number; feeling: string; userId: string; classroomId: string }[]> = {};

  for (const event of events) {
    const userId = event.userId;
    if (!userId) continue;
    if (!byUser[userId]) byUser[userId] = [];
    byUser[userId].push({
      createdAt: typeof event.time === 'number' ? event.time : new Date(event.time).getTime(),
      feeling: (event as any).mood ?? 'No check-in',
      userId,
      classroomId: (event as any).classroomId ?? '',
    });
  }

  return Object.entries(byUser).map(([userId, entries]) => ({
    userIdHash: userId,
    firstName: '',
    lastName: '',
    // Keyed by index string to match the shape MoodboardComponent iterates over
    journals: Object.fromEntries(entries.map((e, i) => [String(i), e])),
  }));
}

/**
 * Transforms Mixpanel journal-mood-log events (from getClassroomMoodScript)
 * into the ClassJournalData shape consumed by MoodboardMiddleSchool:
 *   { studentName, firstName, lastName, Progress: MiddleSchoolCheckInEntry[] }[]
 *
 * Mood is a string from Mixpanel, matching the MiddleSchoolCheckInEntry.feeling: string type.
 */
export function transformMoodEventsToMiddleSchoolJournalData(events: ReportingMixpanelEvent[]): JournalData[] {
  const byUser: Record<string, MiddleSchoolCheckInEntry[]> = {};

  for (const event of events) {
    const userId = event.userId;
    if (!userId) continue;
    if (!byUser[userId]) byUser[userId] = [];
    byUser[userId].push({
      userId,
      createdAt: typeof event.time === 'number' ? event.time : new Date(event.time).getTime(),
      classroomId: (event as any).classroomId ?? '',
      type: 'feelings-checkin',
      feeling: (event as any).mood ?? '',
      reasons: (event as any).reasons ?? '',
    });
  }

  return Object.entries(byUser).map(([userId, entries]) => ({
    studentName: userId,
    firstName: '',
    lastName: '',
    Progress: entries,
  }));
}
